import { useEffect, useRef, useState } from 'react';
import { Alert, Callout, Classes, Dialog, DialogBody } from '@blueprintjs/core';
import { HintButton as Button } from '@/components/base/HintButton';
import { SharingUnavailableError, type LocalShare } from './client';
import { useProjectSharing } from './services';

export function ProjectShareHistory({ onClose }: { onClose: () => void }) {
  const { client, history, link, copyLink } = useProjectSharing();
  const [items, setItems] = useState(() => history.read());
  const operation = useRef<AbortController | undefined>(undefined);
  useEffect(() => () => operation.current?.abort(), []);
  const [target, setTarget] = useState<LocalShare>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const checkStatus = async (item: LocalShare) => {
    if (busy) return;
    const controller = new AbortController();
    operation.current = controller;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await client.reserve(item, controller.signal);
      setItems(history.read());
    } catch (cause) {
      if (controller.signal.aborted) return;
      if (cause instanceof SharingUnavailableError) {
        history.forget(item.requestId);
        setItems(history.read());
        setNotice(
          'The previous share is unavailable, expired or revoked. Its local record has been cleared; you can create a new snapshot.',
        );
      } else {
        setError(
          cause instanceof Error ? cause.message : 'Unable to check share status.',
        );
      }
    } finally {
      setBusy(false);
    }
  };
  const revoke = async () => {
    if (!target || busy) return;
    const controller = new AbortController();
    operation.current = controller;
    setBusy(true);
    setError('');
    try {
      await client.revoke(target, controller.signal);
      setItems(history.read());
      setTarget(undefined);
    } catch (cause) {
      if (controller.signal.aborted) return;
      setError(cause instanceof Error ? cause.message : 'Unable to revoke.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      isOpen
      title="Shared from this browser"
      onClose={onClose}
      className={Classes.DARK}
      style={{ width: 480, maxWidth: 'calc(100vw - 24px)' }}
    >
      <DialogBody>
        <p>
          This history belongs to this browser, not an account. Clearing browser data
          loses link management. Copies already downloaded cannot be recalled.
        </p>
        {notice && (
          <Callout intent="primary" role="status">
            {notice}
          </Callout>
        )}
        {error && (
          <Callout intent="danger" role="alert">
            {error}
          </Callout>
        )}
        {!items.length && <p>No shares recorded in this browser.</p>}
        {items.map((item) => (
          <section key={item.requestId} style={{ marginBlock: 16 }}>
            <strong>{item.title}</strong>
            {item.share?.state === 'ready' ? (
              <>
                <p>
                  {item.share.expiresAt === null
                    ? 'No expiration'
                    : `Expires ${new Date(item.share.expiresAt).toUTCString()}`}
                </p>
                <a href={link(item.share.id)}>Open share</a>{' '}
                <Button
                  onClick={() => {
                    void copyLink(item.share!.id).catch(() =>
                      setError('Copy failed. Use the Open share link.'),
                    );
                  }}
                >
                  Copy link
                </Button>
              </>
            ) : (
              <>
                <p>Upload pending or unconfirmed</p>
                <Button disabled={busy} onClick={() => void checkStatus(item)}>
                  Check status
                </Button>
              </>
            )}{' '}
            <Button intent="danger" onClick={() => setTarget(item)}>
              Revoke
            </Button>
          </section>
        ))}
        <Alert
          isOpen={!!target}
          className={Classes.DARK}
          intent="danger"
          confirmButtonText="Revoke link"
          cancelButtonText="Cancel"
          loading={busy}
          onCancel={() => setTarget(undefined)}
          onConfirm={() => void revoke()}
        >
          <p>
            Revoke the hosted share for {target?.title}? Your local project stays
            unchanged. Previously downloaded copies cannot be deleted.
          </p>
        </Alert>
      </DialogBody>
    </Dialog>
  );
}
